import { ActionTree, GetterTree, MutationTree } from "vuex";
import Vue from "vue";
import { TaxRateView } from "@/shared/models/taxRates";
import { InvoiceDefaultDeliveryDateEnum } from "@/shared/models/enums.type";

let ignoreRefresh = false;
let currentRequest: Promise<any> | undefined = undefined;

const initialState = {
  loadedOn: 0, // ms since epoch, as Date.now()
  model: {
    paysVat: true,
    multiCurrency: true,
    cashiersReportStartingAmount: 0,
    workingToFormalConversionRate: 7.5,
    formalToWorkingConversionRate: 1 / 7.5,
    historicConversionRate: 0,
    bePaymentsInFormalCurrency : true,
    workingCurrency: { id: 2, isoCode: "EUR", name: "Euro" },
    formalCurrency: { id: 1, isoCode: "HRK", name: "Hrvatska kuna" },
    historicCurrency: { id: 0, isoCode: "", name: "" },
    historicCurrencyEndDate: "",
    primaryLanguage: { id: 1, languageCode: "hr", name: "Hrvatski" },
    secondaryLanguage: { id: 2, languageCode: "en", name: "Engleski" },
    taxRates: [] as TaxRateView[],
    outOfScopeTaxRate: {} as TaxRateView,
    exemptTaxRate: {} as TaxRateView,
    standardTaxRate: {} as TaxRateView,
    accomodationUnitTypesTaxRate: {} as TaxRateView,
    downpaymentItemsDefaultTaxRate: {} as TaxRateView,
    invoiceDueDays: 3,
    offerExpirationDays: 7,
    paymentMeansId: null as null | number,
    paymentMeanBaseId: null as null | number,
    showServicePeriod: true,
    enableServicePeriodEditing: false,
    showPaymentSummary: true,
    generatePaymentReferenceNumber: true,
    setFiscalNumberAsMain: false,
    fiscalizeOffers: false,
    fiscalizeDeliveryNotes: false,
    isFiscalizationObligor: false,
    usesExternalFiscalizationService: false,
    isLinkedToExternalFiscalizationService: false,
    numbering: {
      offer: "",
      downPayment: "AV",
      invoice: "",
      cancellation: "",
    },
    tenantId: 0,
    hasLogo: false,
    financialDocumentHeader: "",
    financialDocumentFooter: "",
    financialDocumentPaymentInstructions: "",
    invoicesDefaultDeliveryDate: InvoiceDefaultDeliveryDateEnum.ArrivalDate,
    tenantCanEditPAXCategories: false,
    tenantCanEditLOSCategories: false,
    tenantOBRsUseRelativePricing: false
  },
};

const state = { ...initialState };

export type FinancialSettingsType = typeof state.model;

const getters: GetterTree<typeof state, any> = {
  loadedOn: (state) => state.loadedOn,
  isLoaded: (state) => state.loadedOn > 0,
  settings: (state) => state.loadedOn > 0 ? state.model : undefined,
};

const actions: ActionTree<typeof state, any> = {
  saveModel: async ({ commit }, model) => {
    ignoreRefresh = true;

    try {
      await Vue.axios.put("FinancialSettings", model);
      commit("setModel", model);
    } finally {
      ignoreRefresh = false;
    }
  },
  refreshModel: async ({ commit }) => {
    // in case it's already loading, this will stop making another call
    if (ignoreRefresh) {
      return currentRequest;
    }

    ignoreRefresh = true;

    try {
      const loadPromise = Vue.axios
        .get<typeof state.model>("FinancialSettings")
        .then((x) => x.data);
      currentRequest = loadPromise;
      const res = await loadPromise;

      const taxRates = res.taxRates || [];

      res.outOfScopeTaxRate =
        taxRates.find(e => e.isOutOfScope) ||
        taxRates.find(e => e.isStandard) ||
        taxRates[0] || {};

      res.standardTaxRate =
        taxRates.find(e => e.isStandard) ||
        taxRates[0] || {};

      res.exemptTaxRate =
        taxRates.find(e => e.isTaxExempt) ||
        taxRates.find(e => e.isStandard) ||
        taxRates[0] || {};

      res.accomodationUnitTypesTaxRate =
        taxRates.find((e) => e.id === (res.accomodationUnitTypesTaxRate?.id ?? 2)) ||
        taxRates.find((e) => e.isStandard) ||
        taxRates[0] || {};
  
      res.downpaymentItemsDefaultTaxRate =
        taxRates.find((e) => e.id === (res.downpaymentItemsDefaultTaxRate?.id ?? 1)) ||
        taxRates.find((e) => e.isStandard) ||
        taxRates[0] || {};

      commit("setModel", res);
    } finally {
      currentRequest = undefined;
      ignoreRefresh = false;
    }
  },
};

const mutations: MutationTree<typeof state> = {
  reset: () => {
    Object.assign(state, initialState);
  },
  setModel: (state, value: typeof state.model) => {
    state.loadedOn = Date.now();
    state.model = value;

    state.model.taxRates = value.taxRates || [];
    state.model.outOfScopeTaxRate = value.outOfScopeTaxRate || {};
    state.model.standardTaxRate = value.standardTaxRate || {};
    state.model.exemptTaxRate = value.exemptTaxRate || {};
    state.model.accomodationUnitTypesTaxRate = value.accomodationUnitTypesTaxRate || {};
    state.model.downpaymentItemsDefaultTaxRate = value.downpaymentItemsDefaultTaxRate || {}; 
  },
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations,
};
